/**
 * Cloudinary Upload Utility
 *
 * Handles client-side uploads to Cloudinary.
 * Prefers signed uploads (via /api/cloudinary/sign) which remove the 100MB
 * file size limit. Falls back to unsigned uploads if the sign endpoint is
 * unavailable.
 *
 * Files over CHUNK_THRESHOLD are uploaded in chunks using Cloudinary's
 * chunked upload API to avoid connection drops on large files.
 */

// Cloudinary configuration
const CLOUD_NAME = 'fingerstyle';
const UPLOAD_PRESET = 'fingerstyle_uploads';

// Files larger than 20MB use chunked upload
const CHUNK_THRESHOLD = 20 * 1024 * 1024;
// 6MB chunks (Cloudinary minimum is 5MB, except the last chunk)
const CHUNK_SIZE = 6 * 1024 * 1024;

export interface CloudinaryUploadResult {
  url: string;
  secureUrl: string;
  publicId: string;
  format: string;
  resourceType: string;
  bytes: number;
  width?: number;
  height?: number;
  duration?: number;
}

export interface UploadProgress {
  loaded: number;
  total: number;
  percent: number;
}

interface SignatureResponse {
  signature: string;
  timestamp: number;
  api_key: string;
  cloud_name: string;
}

/**
 * Detect if a file is video/audio based on MIME type or filename extension
 */
function detectFileType(file: File): { isVideo: boolean; isAudio: boolean } {
  // Try MIME type first
  if (file.type) {
    return {
      isVideo: file.type.startsWith('video/'),
      isAudio: file.type.startsWith('audio/'),
    };
  }

  // Fallback to filename extension (with defensive check)
  const fileName = file.name || '';
  const ext = fileName.split('.').pop()?.toLowerCase() || '';
  const videoExtensions = ['mp4', 'mov', 'avi', 'webm', 'mkv', 'm4v'];
  const audioExtensions = ['mp3', 'wav', 'ogg', 'm4a', 'aac', 'flac'];

  return {
    isVideo: videoExtensions.includes(ext),
    isAudio: audioExtensions.includes(ext),
  };
}

/**
 * Fetch a signed upload signature from the backend.
 * Returns null if the sign endpoint is unavailable.
 */
async function fetchSignature(folder?: string): Promise<SignatureResponse | null> {
  try {
    const params = new URLSearchParams();
    if (folder) {
      params.set('folder', folder);
    }
    const url = `/api/cloudinary/sign${params.toString() ? `?${params}` : ''}`;
    const response = await fetch(url);
    if (!response.ok) {
      console.warn(`[Cloudinary] Sign endpoint returned ${response.status}, falling back to unsigned upload`);
      return null;
    }
    return await response.json();
  } catch (err) {
    console.warn('[Cloudinary] Failed to fetch upload signature, falling back to unsigned upload:', err);
    return null;
  }
}

/**
 * Generate a random unique ID for chunked upload sessions
 */
function generateUploadId(): string {
  return `uqid-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

/**
 * Build common form data fields for an upload request
 */
function buildFormData(
  signature: SignatureResponse | null,
  folder: string
): { formFields: Record<string, string>; cloudName: string; uploadMode: string } {
  const formFields: Record<string, string> = {};
  let cloudName: string;
  let uploadMode: string;

  if (signature) {
    uploadMode = 'signed';
    formFields['signature'] = signature.signature;
    formFields['timestamp'] = String(signature.timestamp);
    formFields['api_key'] = signature.api_key;
    if (folder) {
      formFields['folder'] = folder;
    }
    cloudName = signature.cloud_name;
  } else {
    uploadMode = 'unsigned';
    formFields['upload_preset'] = UPLOAD_PRESET;
    formFields['folder'] = folder;
    cloudName = CLOUD_NAME;
  }

  return { formFields, cloudName, uploadMode };
}

/**
 * Parse the Cloudinary JSON response into our result type
 */
function parseCloudinaryResponse(response: Record<string, unknown>): CloudinaryUploadResult {
  return {
    url: response.url as string,
    secureUrl: response.secure_url as string,
    publicId: response.public_id as string,
    format: response.format as string,
    resourceType: response.resource_type as string,
    bytes: response.bytes as number,
    width: response.width as number | undefined,
    height: response.height as number | undefined,
    duration: response.duration as number | undefined,
  };
}

/**
 * Upload a single chunk via XHR with Content-Range header.
 * Returns the parsed JSON response from Cloudinary.
 */
function uploadChunk(
  url: string,
  formFields: Record<string, string>,
  chunk: Blob,
  chunkStart: number,
  chunkEnd: number,
  totalSize: number,
  uploadId: string,
): Promise<Record<string, unknown>> {
  return new Promise((resolve, reject) => {
    const formData = new FormData();
    formData.append('file', chunk);
    for (const [key, value] of Object.entries(formFields)) {
      formData.append(key, value);
    }

    const xhr = new XMLHttpRequest();

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText));
        } catch {
          reject(new Error(`Failed to parse chunk response: ${xhr.responseText.slice(0, 200)}`));
        }
      } else {
        let errorMessage = `Chunk upload failed (HTTP ${xhr.status})`;
        try {
          const resp = JSON.parse(xhr.responseText);
          errorMessage = resp.error?.message || errorMessage;
        } catch {
          // Ignore parse error
        }
        reject(new Error(errorMessage));
      }
    });

    xhr.addEventListener('error', () => {
      console.error('[Cloudinary] Chunk XHR error:', {
        status: xhr.status,
        chunkStart,
        chunkEnd,
        totalSize,
      });
      reject(new Error(`Network error uploading chunk (bytes ${chunkStart}-${chunkEnd}/${totalSize})`));
    });

    xhr.addEventListener('abort', () => reject(new Error('Chunk upload aborted')));

    xhr.timeout = 120000; // 2 minutes per chunk
    xhr.ontimeout = () => reject(new Error(`Chunk upload timed out (bytes ${chunkStart}-${chunkEnd})`));

    xhr.open('POST', url);
    xhr.setRequestHeader('X-Unique-Upload-Id', uploadId);
    xhr.setRequestHeader('Content-Range', `bytes ${chunkStart}-${chunkEnd}/${totalSize}`);
    xhr.send(formData);
  });
}

/**
 * Upload a large file in chunks using Cloudinary's chunked upload API.
 */
async function uploadChunked(
  file: File,
  url: string,
  formFields: Record<string, string>,
  onProgress?: (progress: UploadProgress) => void,
): Promise<CloudinaryUploadResult> {
  const totalSize = file.size;
  const uploadId = generateUploadId();
  const totalChunks = Math.ceil(totalSize / CHUNK_SIZE);

  console.log(`[Cloudinary] Chunked upload: ${totalChunks} chunks of ${(CHUNK_SIZE / 1024 / 1024).toFixed(0)}MB`);

  let lastResponse: Record<string, unknown> = {};

  for (let i = 0; i < totalChunks; i++) {
    const start = i * CHUNK_SIZE;
    const end = Math.min(start + CHUNK_SIZE, totalSize) - 1;
    const chunk = file.slice(start, end + 1);

    console.log(`[Cloudinary] Uploading chunk ${i + 1}/${totalChunks} (bytes ${start}-${end})`);

    lastResponse = await uploadChunk(url, formFields, chunk, start, end, totalSize, uploadId);

    if (onProgress) {
      onProgress({
        loaded: end + 1,
        total: totalSize,
        percent: Math.round(((end + 1) / totalSize) * 100),
      });
    }
  }

  return parseCloudinaryResponse(lastResponse);
}

/**
 * Upload a file in a single request (for smaller files).
 */
function uploadSingle(
  file: File,
  url: string,
  formFields: Record<string, string>,
  onProgress?: (progress: UploadProgress) => void,
): Promise<CloudinaryUploadResult> {
  return new Promise((resolve, reject) => {
    const formData = new FormData();
    formData.append('file', file);
    for (const [key, value] of Object.entries(formFields)) {
      formData.append(key, value);
    }

    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress({
          loaded: event.loaded,
          total: event.total,
          percent: Math.round((event.loaded / event.total) * 100),
        });
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(parseCloudinaryResponse(JSON.parse(xhr.responseText)));
      } else {
        let errorMessage = 'Upload failed';
        try {
          const response = JSON.parse(xhr.responseText);
          errorMessage = response.error?.message || errorMessage;
        } catch {
          // Ignore parse error
        }
        reject(new Error(errorMessage));
      }
    });

    xhr.addEventListener('error', () => {
      console.error('[Cloudinary] XHR error event:', {
        status: xhr.status,
        statusText: xhr.statusText,
        responseText: xhr.responseText,
        readyState: xhr.readyState,
        url,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
      });
      reject(new Error(`Network error during upload (file: ${file.name}, size: ${(file.size / 1024 / 1024).toFixed(2)}MB)`));
    });

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload aborted'));
    });

    xhr.timeout = 600000; // 10 minute timeout
    xhr.ontimeout = () => reject(new Error('Upload timed out'));

    xhr.open('POST', url);
    xhr.send(formData);
  });
}

/**
 * Upload a file to Cloudinary
 *
 * Tries signed upload first (no file size limit). If the signature endpoint
 * is unavailable, falls back to unsigned upload (100MB limit).
 *
 * Files over 20MB are uploaded in chunks to avoid connection drops.
 *
 * @param file - The file to upload
 * @param folder - The folder path in Cloudinary (e.g., "fingerstyle/capsules/my-song")
 * @param onProgress - Optional progress callback
 * @returns The upload result with URL and metadata
 */
export async function uploadToCloudinary(
  file: File,
  folder: string,
  onProgress?: (progress: UploadProgress) => void
): Promise<CloudinaryUploadResult> {
  const signature = await fetchSignature(folder);
  const { formFields, cloudName, uploadMode } = buildFormData(signature, folder);

  // Determine resource type based on file type or extension
  const { isVideo, isAudio } = detectFileType(file);
  const resourceType = isVideo ? 'video' : isAudio ? 'video' : 'auto'; // Cloudinary uses 'video' for audio too

  const url = `https://api.cloudinary.com/v1_1/${cloudName}/${resourceType}/upload`;

  const useChunked = file.size > CHUNK_THRESHOLD;
  console.log(`[Cloudinary] Starting ${uploadMode} ${useChunked ? 'chunked' : 'single'} upload: ${file.name} (${(file.size / 1024 / 1024).toFixed(1)}MB) to ${cloudName}`);

  if (useChunked) {
    return uploadChunked(file, url, formFields, onProgress);
  } else {
    return uploadSingle(file, url, formFields, onProgress);
  }
}

/**
 * Check if we should use Cloudinary (production) or local dev server
 */
export function shouldUseCloudinary(): boolean {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  return hostname !== 'localhost' && hostname !== '127.0.0.1';
}

/**
 * Generate an optimized URL for a Cloudinary asset
 */
export function getOptimizedUrl(publicId: string, options?: {
  width?: number;
  height?: number;
  quality?: string;
  format?: string;
}): string {
  const transformations: string[] = [];

  if (options?.width) transformations.push(`w_${options.width}`);
  if (options?.height) transformations.push(`h_${options.height}`);
  transformations.push(`q_${options?.quality || 'auto'}`);
  transformations.push(`f_${options?.format || 'auto'}`);

  const transformStr = transformations.join(',');
  return `https://res.cloudinary.com/${CLOUD_NAME}/video/upload/${transformStr}/${publicId}`;
}
